import { useEffect, useId, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Dialog, Typography } from '@mui/material';
import { LoadFailure } from '../../components/LoadFailure';
import { fetchGenerationSettings } from '../../core/thresholdsApi';
import type { Thresholds } from '../../core/thresholds';
import { DATA_REVIEW, MATCHES_ROUTE } from '../Data/dataReviewTokens';
import { fetchMatches, type MatchesState } from '../Data/matchesApi';
import { heldOutUntilReviewed } from '../Data/matchRows';
import { COLORS } from '../IndividualPlan/tokens';
import { markPlansGenerated } from '../Plans/plansGenerated';
import { ROSTER } from '../Roster/tokens';
import { PRIMARY_BUTTON_SX, TEXT_BUTTON_SX } from '../Samples/SampleBanner';
import {
  confirmedLabel,
  criteriaHeading,
  criteriaRows,
  fetchExitCriteria,
  yearsRows,
  type ExitCriteria,
  type SettingRow,
} from './confirmedSettings';

export const SETTINGS_EXIT_CRITERIA_ROUTE = '/settings/exit-criteria';

type Props = {
  open: boolean;
  studentCount: number;
  onClose: () => void;
  onGenerated: (count: number) => void;
  exitCriteriaSource?: () => Promise<ExitCriteria>;
  yearsSource?: () => Promise<Thresholds>;
  matchesSource?: () => Promise<MatchesState>;
  generate?: () => Promise<unknown>;
};

type ConfirmedSettings = { criteria: ExitCriteria; years: Thresholds };

function SettingsSection({ heading, confirmed, rows }: { heading: string; confirmed: string | null; rows: SettingRow[] }) {
  return (
    <Box
      component="section"
      aria-label={heading}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '16px',
        border: `1px solid ${ROSTER.border}`,
        borderRadius: '8px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '16px' }}>
        <Typography component="h3" sx={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: COLORS.ink }}>
          {heading}
        </Typography>
        {confirmed && (
          <Typography component="small" sx={{ fontSize: 13, lineHeight: '20px', color: COLORS.muted }}>
            {confirmed}
          </Typography>
        )}
      </Box>
      {rows.map((row) => (
        <Box key={row.label}>
          <Box sx={{ display: 'flex', gap: '16px', fontSize: 16, lineHeight: '24px', color: COLORS.ink }}>
            <Box component="span" sx={{ width: 200, flexShrink: 0, color: COLORS.muted }}>
              {row.label}
            </Box>
            <Box component="span" sx={{ flex: '1 1 0', minWidth: 0, textAlign: 'right', fontWeight: 700 }}>
              {row.value}
            </Box>
          </Box>
          {row.note && (
            <Typography sx={{ mt: '2px', fontSize: 13, lineHeight: '20px', color: COLORS.muted, textAlign: 'right' }}>
              {row.note}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

type ContentProps = Omit<Props, 'open'> &
  Required<Pick<Props, 'exitCriteriaSource' | 'yearsSource' | 'matchesSource' | 'generate'>> & {
    titleId: string;
    onGeneratingChange: (generating: boolean) => void;
  };

function GeneratePlansContent({
  studentCount,
  onClose,
  onGenerated,
  exitCriteriaSource,
  yearsSource,
  matchesSource,
  generate,
  titleId,
  onGeneratingChange,
}: ContentProps) {
  const [settings, setSettings] = useState<ConfirmedSettings | null>(null);
  const [matches, setMatches] = useState<MatchesState | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateFailed, setGenerateFailed] = useState(false);
  const generateButton = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([exitCriteriaSource(), yearsSource()])
      .then(([criteria, years]) => {
        if (active) setSettings({ criteria, years });
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      });
    return () => {
      active = false;
    };
  }, [exitCriteriaSource, yearsSource]);

  useEffect(() => {
    let active = true;
    matchesSource()
      .then((next) => {
        if (active) setMatches(next);
      })
      .catch((err: unknown) => console.warn('generate plans: matches unavailable', err));
    return () => {
      active = false;
    };
  }, [matchesSource]);

  const setPending = (pending: boolean) => {
    setGenerating(pending);
    onGeneratingChange(pending);
  };

  const runGenerate = () => {
    setPending(true);
    setGenerateFailed(false);
    generate()
      .then(() => {
        setPending(false);
        onGenerated(studentCount);
      })
      .catch(() => {
        setPending(false);
        setGenerateFailed(true);
      });
  };

  const confirmed =
    settings !== null &&
    settings.criteria.exitCriteriaConfirmed !== null &&
    settings.criteria.yearsConfirmed !== null;
  const ready = confirmed && !generating;
  const close = () => {
    if (!generating) onClose();
  };

  useEffect(() => {
    if (ready) generateButton.current?.focus();
  }, [ready]);

  return (
    <>
      <Box>
        <Typography id={titleId} component="h2" sx={{ fontSize: 24, lineHeight: '32px', fontWeight: 700, color: COLORS.ink }}>
          Generate plans for {studentCount} students?
        </Typography>
        <Typography sx={{ mt: '4px', fontSize: 16, lineHeight: '24px', color: COLORS.muted }}>
          Every plan uses your confirmed settings.
        </Typography>
      </Box>
      {loadFailed && <LoadFailure tokens={ROSTER} />}
      {settings && (
        <>
          <SettingsSection
            heading={criteriaHeading(settings.criteria)}
            confirmed={confirmedLabel(settings.criteria.exitCriteriaConfirmed)}
            rows={criteriaRows(settings.criteria.gradeBands)}
          />
          <SettingsSection
            heading="Newcomer and long-term years"
            confirmed={confirmedLabel(settings.criteria.yearsConfirmed)}
            rows={yearsRows(settings.years)}
          />
        </>
      )}
      {matches !== null && matches.toReview > 0 && (
        <Box
          role="status"
          data-testid="held-out-line"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            bgcolor: DATA_REVIEW.stripBg,
            color: DATA_REVIEW.stripFg,
            fontSize: 15,
            lineHeight: '22px',
          }}
        >
          <Box component="span" sx={{ flex: '1 1 auto' }}>
            {heldOutUntilReviewed(matches.heldOutStudents)}
          </Box>
          <Box
            component={RouterLink}
            to={MATCHES_ROUTE}
            aria-disabled={generating ? true : undefined}
            onClick={(event: React.MouseEvent) => {
              if (generating) {
                event.preventDefault();
                return;
              }
              onClose();
            }}
            sx={{ color: 'inherit', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Review matches
          </Box>
        </Box>
      )}
      {generateFailed && (
        <Typography role="alert" sx={{ fontSize: 16, lineHeight: '24px', color: COLORS.ink }}>
          Couldn’t generate plans. Try again.
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pt: '4px' }}>
        <Box
          component={RouterLink}
          to={SETTINGS_EXIT_CRITERIA_ROUTE}
          aria-disabled={generating ? true : undefined}
          onClick={(event: React.MouseEvent) => {
            if (generating) {
              event.preventDefault();
              return;
            }
            onClose();
          }}
          sx={{ color: COLORS.navy, fontSize: 16, lineHeight: '24px', fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          Change in Settings
        </Box>
        <Box sx={{ flex: '1 1 auto' }} />
        <Box
          component="button"
          type="button"
          disabled={generating}
          onClick={close}
          sx={{ ...TEXT_BUTTON_SX, border: 0, cursor: 'pointer', '&:disabled': { color: COLORS.muted, cursor: 'default' } }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          type="button"
          ref={generateButton}
          disabled={!ready}
          onClick={runGenerate}
          sx={{ ...PRIMARY_BUTTON_SX, '&:disabled': { bgcolor: COLORS.muted, cursor: 'default' } }}
        >
          Generate plans
        </Box>
      </Box>
    </>
  );
}

export function GeneratePlansDialog({
  open,
  studentCount,
  onClose,
  onGenerated,
  exitCriteriaSource = fetchExitCriteria,
  yearsSource = fetchGenerationSettings,
  matchesSource = fetchMatches,
  generate = markPlansGenerated,
}: Props) {
  const titleId = useId();
  const [generating, setGenerating] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!generating) onClose();
      }}
      aria-labelledby={titleId}
      PaperProps={{
        sx: { width: 600, maxWidth: 'calc(100vw - 48px)', m: 0, p: '32px', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '20px' },
      }}
    >
      <GeneratePlansContent
        studentCount={studentCount}
        onClose={onClose}
        onGenerated={onGenerated}
        exitCriteriaSource={exitCriteriaSource}
        yearsSource={yearsSource}
        matchesSource={matchesSource}
        generate={generate}
        titleId={titleId}
        onGeneratingChange={setGenerating}
      />
    </Dialog>
  );
}
